import React, { useRef, useEffect, useMemo } from "react";
import { View, TouchableOpacity } from "react-native";
import { getScrollableNodeFromRef } from "../utils/floatingUtils";
import { buildExerciseNameWithModifiers } from "../utils/searchUtils";
import { HighlightedText } from "./HighlightedText";
import { Pill } from "./Pill";

/**
 * Suggestion Row component
 */
export const SuggestionRow = ({
  result,
  isSelected,
  onSelect,
  onHover,
  searchQuery,
  t,
  language,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
}) => {
  const rowRef = useRef(null);

  // Build the full exercise name with modifiers using getName logic
  const fullName = useMemo(() => {
    return buildExerciseNameWithModifiers(
      result.exerciseName,
      result.modifiers,
      language
    );
  }, [result.exerciseName, result.modifiers, language]);

  // Scroll into view when selected
  useEffect(() => {
    if (
      isSelected &&
      keyboardNavigationRef?.current &&
      rowRef.current &&
      scrollContainerRef?.current
    ) {
      const isFirstItem = index === 0;
      const isLastItem = index === totalResults - 1;
      const rowElement = rowRef.current;

      const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

      if (isFirstItem) {
        scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        return;
      }

      if (isLastItem) {
        scrollContainerRef.current?.scrollToEnd?.({ animated: true });
        if (scrollContainer) {
          const containerHeight = scrollContainer.clientHeight;
          const scrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = scrollHeight - containerHeight;
        }
        return;
      }

      rowElement.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    isSelected,
    index,
    totalResults,
    scrollContainerRef,
    keyboardNavigationRef,
  ]);

  return (
    <TouchableOpacity
      ref={rowRef}
      onPress={onSelect}
      onMouseEnter={onHover}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginHorizontal: 8,
        borderRadius: 10,
        backgroundColor: isSelected
          ? "rgba(63, 123, 141, 0.08)"
          : "transparent",
        outlineStyle: "none",
      }}
    >
      {/* Left: Exercise name with highlighted matching characters */}
      <View
        style={{
          flexGrow: 1,
          flexShrink: 1,
          marginRight: 10,
          minWidth: 0,
        }}
      >
        <HighlightedText text={fullName} searchQuery={searchQuery} />
      </View>

      {/* Right: Pills */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 6,
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        {/* Main exercise name pill */}
        <Pill label={result.exerciseName} type="exercise" />

        {/* Modifier pills */}
        {result.modifiers &&
          result.modifiers.map((modifier, idx) => (
            <Pill
              key={modifier.id || idx}
              label={modifier.name}
              type="modifier"
            />
          ))}
      </View>
    </TouchableOpacity>
  );
};
